/** @jsxRuntime classic */
/** @jsx jsx */
import { memo, useRef, useEffect, forwardRef, Children, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import theme from "../../../theme/settings";
const {
  spacing
} = theme;
const FormGroup = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  fullWidth,
  oneHelperText,
  marginRight,
  className,
  children,
  ...props
}, reference) => {
  const ref = useRef(null);
  const previousError = useRef(false);

  /* Start styled */
  const FormGroupRoot = css`
            position: relative;
            align-items: flex-end;
            box-sizing: border-box;
            width: auto;
            margin-bottom: ${spacing(4)}px;
            &.Form-Group-full-width {
                width: 100% !important;
                flex: 1 1 auto;
                .DGN-UI-TextInput {
                    flex: 1 1 auto;
                }
            }
            &.Form-Group-Common {
                margin-bottom: 20px;
                width: max-content;
                .DGN-UI-HelperText {
                    width: 100%;
                    left: 0;
                }
                .DGN-UI-TextInput {
                    position: initial;
                    margin-bottom: 0 !important;
                }
            }
            &.DGN-UI-TextInput {
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                height: 100%;
                margin-bottom: 20px !important;
                &:not(:last-of-type) {
                    margin-right: ${isNaN(marginRight) ? marginRight : marginRight + 'px'};
                }
            }
        `;
  /* End styled */

  /* Start handler */
  const resetLabelPosition = () => {
    const allUnderlined = ref.current.querySelectorAll('.DGN-UI-InputBase.underlined');
    const outlined = ref.current.querySelector('.DGN-UI-InputBase.outlined');
    if (allUnderlined.length && outlined) {
      allUnderlined.forEach(input => {
        if (input.previousElementSibling) {
          input.previousElementSibling.style.marginBottom = '10px';
        }
      });
    }
  };
  useEffect(() => {
    if (ref.current) {
      resetLabelPosition();
    }
  }, []);
  useEffect(() => {
    if (oneHelperText) {
      let isError = false;
      Children.forEach(children, child => {
        if (!isError && !!child.props.error) {
          isError = true;
        }
      });
      if (previousError.current !== isError) {
        previousError.current = isError;
        ref.current.querySelectorAll('.DGN-UI-TextInput, .DGN-UI-Dropdown, .DGN-UI-InputBase').forEach(node => {
          node.classList[isError ? 'add' : 'remove']('danger');
        });
      }
    }
  }, [children]);
  /* End handler */

  useImperativeHandle(reference, () => ref.current);
  return jsx("div", {
    ref: ref,
    css: FormGroupRoot,
    className: ['DGN-UI-FormGroup', fullWidth ? 'Form-Group-full-width' : '', oneHelperText ? 'Form-Group-Common' : '', className].join(' ').trim(),
    ...props
  }, children);
}));
FormGroup.defaultProps = {
  marginRight: 16,
  fullWidth: false,
  oneHelperText: false,
  className: ''
};
FormGroup.propTypes = {
  fullWidth: PropTypes.bool,
  oneHelperText: PropTypes.bool,
  marginRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  children: PropTypes.node.isRequired
};
export default FormGroup;